import { useContext, useEffect } from "react";

import { FormInstance } from "antd";
import { useDispatch } from "react-redux";

import { ConditionsList } from "Pages/audience/Pages/SegmentsPage/Pages/SegmentEditPage/ConditionsList/ConditionsList";
import { UsedInFeature } from "Pages/audience/Pages/SegmentsPage/Pages/SegmentEditPage/ConditionsList/Shared/useEventNames";
import { useFlags } from "Services/FeatureFlagService";
import { setFields } from "Services/state/journeys/JourneyStateSlice";
import { NodeTriggerType } from "Types/Journey";
import {
    ConditionEvent,
    ConditionList,
    DynamicCondition,
    EventProperty,
    JoinType,
    NamedConditionalTypes,
} from "Types/Segment";

import { JourneyFormContext } from "../../../../../shared/Contexts/JourneyFormContext";
import { triggerTypeDisplayName } from "../../../../../shared/helpers";
import { unflattenFields } from "../../Utils/utils";

import type { ConditionalOptions } from "Pages/audience/Pages/SegmentsPage/Pages/SegmentEditPage/ConditionsList/Types";

const conditionalTypes = ({
    journeyBuilderAllowPageviewTrigger,
}: {
    journeyBuilderAllowPageviewTrigger: boolean;
}): NamedConditionalTypes => {
    return [
        { value: NodeTriggerType.Custom, name: triggerTypeDisplayName(NodeTriggerType.Custom) },
        { value: NodeTriggerType.RegalVoice, name: triggerTypeDisplayName(NodeTriggerType.RegalVoice) },
        ...(journeyBuilderAllowPageviewTrigger
            ? [
                  {
                      value: NodeTriggerType.Pageview,
                      name: triggerTypeDisplayName(NodeTriggerType.Pageview),
                      hideCustomFields: true,
                  },
              ]
            : []),
        {
            value: NodeTriggerType.Scheduled,
            name: triggerTypeDisplayName(NodeTriggerType.Scheduled),
            hideCustomFields: true,
        },
    ];
};

const propertyDefaults: EventProperty = { propertyOperator: "isSet" };
const eventDefaults: ConditionEvent = {
    eventOperator: "isSet",
    filters: [propertyDefaults],
    lastEventOnly: false,
};
const conditionDefaults: DynamicCondition = {
    joinType: JoinType.ALL,
    conditions: [eventDefaults],
};

const conditionalOptions: ConditionalOptions = {
    source: UsedInFeature.TriggeringNode,
    dynamicConditions: [conditionDefaults],
    conditionDefaults,
    joinType: JoinType.ALL,
    direction: "vertical",
    groupName: "Trigger Event",
    hideEventOperatorSelector: true,
    allowCustomEventName: true,
    conditionalListOptions: {
        hideJoinSelect: true,
        hideAddItemButton: true,
        hideCollapse: true,
        hideConditionRemove: false,
        hideConditionHeader: true,
        hideLastEventOnly: true,
    },
    eventListOptions: { hideJoinSelect: true, hideAddItemButton: true },
};

export function TriggerEventConditions({
    form,
    formValues,
    triggerType,
}: {
    form: FormInstance;
    formValues: Record<string, any>;
    triggerType: NodeTriggerType;
}): JSX.Element {
    const dispatch = useDispatch();
    const { segmentId } = formValues;
    const { formSubmited } = useContext(JourneyFormContext);
    const { journeyBuilderAllowPageviewTrigger } = useFlags();

    const dynamicConditions = (formValues || { dynamicConditions: [conditionDefaults] }) as ConditionList;

    useEffect(() => {
        function handleAddContactFilter() {
            dispatch(
                setFields(
                    unflattenFields({
                        ...formValues,
                        triggerFiltersEvents: [conditionDefaults],
                    })
                )
            );
        }
        if (!formValues.triggerFiltersEvents) {
            handleAddContactFilter();
        }
    }, [formValues]);

    useEffect(() => {
        if (segmentId && triggerType !== NodeTriggerType.Scheduled) {
            dispatch(
                setFields(
                    unflattenFields({
                        ...formValues,
                        segmentId: undefined,
                        segmentName: undefined,
                        scheduledJourneyStartDate: undefined,
                    })
                )
            );
        }
    }, [triggerType, segmentId]);

    return (
        <div>
            <ConditionsList
                form={form}
                data={dynamicConditions}
                fieldName="triggerFiltersEvents"
                conditionalTypes={conditionalTypes({
                    journeyBuilderAllowPageviewTrigger,
                })}
                conditionalOptions={conditionalOptions}
                validationEnabled={formSubmited}
                autoCollapse={false}
            />
        </div>
    );
}
